import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import LogoMark from "./LogoMark";
import { api } from "../api";

const TikTokIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1-2.89-2.89 2.89 2.89 0 0 1 2.89-2.89c.28 0 .54.04.79.1V9.01a6.27 6.27 0 0 0-.79-.05 6.34 6.34 0 0 0-6.34 6.34 6.34 6.34 0 0 0 6.34 6.34 6.34 6.34 0 0 0 6.33-6.34V8.77a8.23 8.23 0 0 0 4.82 1.55V6.86a4.85 4.85 0 0 1-1.05-.17z"/>
  </svg>
);

const FacebookIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
    <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"/>
  </svg>
);

const LinkedInIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
    <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z"/>
    <rect x="2" y="9" width="4" height="12"/>
    <circle cx="4" cy="4" r="2"/>
  </svg>
);

const InstagramIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="2" width="20" height="20" rx="5" ry="5"/>
    <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/>
    <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/>
  </svg>
);

const YouTubeIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
    <path d="M22.54 6.42a2.78 2.78 0 0 0-1.95-1.96C18.88 4 12 4 12 4s-6.88 0-8.59.46A2.78 2.78 0 0 0 1.46 6.42 29 29 0 0 0 1 12a29 29 0 0 0 .46 5.58 2.78 2.78 0 0 0 1.95 1.96C5.12 20 12 20 12 20s6.88 0 8.59-.46a2.78 2.78 0 0 0 1.95-1.96A29 29 0 0 0 23 12a29 29 0 0 0-.46-5.58z"/>
    <polygon points="9.75 15.02 15.5 12 9.75 8.98 9.75 15.02" fill="#0B0B0B"/>
  </svg>
);

const SOCIAL_KEYS = [
  { label: "TikTok",    Icon: TikTokIcon,    key: "tiktok_url" },
  { label: "Facebook",  Icon: FacebookIcon,  key: "facebook_url" },
  { label: "LinkedIn",  Icon: LinkedInIcon,  key: "linkedin_url" },
  { label: "Instagram", Icon: InstagramIcon, key: "instagram_url" },
  { label: "YouTube",   Icon: YouTubeIcon,   key: "youtube_url" },
];

export default function Footer() {
  const [socials, setSocials] = useState([]);

  useEffect(() => {
    api.getSettings().then(s => {
      const links = SOCIAL_KEYS
        .map(item => ({ ...item, href: s[item.key] || "" }))
        .filter(item => item.href);
      setSocials(links);
    }).catch(() => {});
  }, []);

  return (
    <footer style={{ background: "#0B0B0B", color: "#fff", padding: "4rem 2rem 2rem" }}>
      <div className="container">
        {/* Top row */}
        <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr 1fr", gap: "3rem", marginBottom: "3rem" }} className="mobile-full">
          {/* Brand */}
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", marginBottom: "1rem" }}>
              <LogoMark size={44} showBackground={true} />
              <span style={{ fontFamily: "var(--font)", fontWeight: 800, fontSize: "1.1rem" }}>The Health Plug</span>
            </div>
            <p style={{ color: "rgba(255,255,255,0.45)", fontSize: "0.85rem", lineHeight: 1.7, fontFamily: "var(--font-body)", maxWidth: 280 }}>
              Making health information accessible, multilingual and entertaining.
            </p>
            {/* Socials */}
            <div style={{ display: "flex", gap: "0.75rem", marginTop: "1.25rem", flexWrap: "wrap" }}>
              {socials.map(s => (
                <a key={s.label} href={s.href} title={s.label} target="_blank" rel="noopener noreferrer"
                  style={{ width: 36, height: 36, borderRadius: "50%", background: "rgba(255,255,255,0.08)", display: "flex", alignItems: "center", justifyContent: "center", color: "rgba(255,255,255,0.7)", transition: "background 0.2s, color 0.2s" }}
                  onMouseEnter={e => { e.currentTarget.style.background = "#8A1613"; e.currentTarget.style.color = "#fff"; }}
                  onMouseLeave={e => { e.currentTarget.style.background = "rgba(255,255,255,0.08)"; e.currentTarget.style.color = "rgba(255,255,255,0.7)"; }}
                ><s.Icon /></a>
              ))}
            </div>
          </div>

          {/* Quick links */}
          <div>
            <p style={{ fontFamily: "var(--font)", fontWeight: 700, fontSize: "0.85rem", letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: "1rem", color: "#95BB83" }}>Pages</p>
            {[["Home","/"],["Our Why","/our-why"],["Services","/services"],["Meet the Team","/team"],["Clients","/clients"],["Contact","/contact"]].map(([lbl,to]) => (
              <Link key={to} to={to} style={{ display: "block", color: "rgba(255,255,255,0.5)", fontSize: "0.85rem", marginBottom: "0.5rem", transition: "color 0.2s" }}
                onMouseEnter={e => e.target.style.color="#fff"}
                onMouseLeave={e => e.target.style.color="rgba(255,255,255,0.5)"}
              >{lbl}</Link>
            ))}
          </div>

          {/* Services */}
          <div>
            <p style={{ fontFamily: "var(--font)", fontWeight: 700, fontSize: "0.85rem", letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: "1rem", color: "#95BB83" }}>Services</p>
            {["Health Campaigns","Multimedia Content","PR & Media","Podcast Content","Community Activations","Multilingual Comms"].map(s => (
              <p key={s} style={{ color: "rgba(255,255,255,0.5)", fontSize: "0.85rem", marginBottom: "0.5rem" }}>{s}</p>
            ))}
          </div>

          {/* Contact */}
          <div>
            <p style={{ fontFamily: "var(--font)", fontWeight: 700, fontSize: "0.85rem", letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: "1rem", color: "#95BB83" }}>Contact</p>
            <p style={{ color: "rgba(255,255,255,0.5)", fontSize: "0.85rem", marginBottom: "0.5rem" }}>📧 info@thehealthplug.co.za</p>
            <p style={{ color: "rgba(255,255,255,0.5)", fontSize: "0.85rem", marginBottom: "0.5rem" }}>📱 WhatsApp available</p>
            <p style={{ color: "rgba(255,255,255,0.5)", fontSize: "0.85rem", marginBottom: "1.5rem" }}>📍 South Africa</p>
            <Link to="/contact" className="btn-red" style={{ padding: "0.6rem 1.25rem", fontSize: "0.82rem" }}>Schedule a Call</Link>
          </div>
        </div>

        {/* Bottom bar */}
        <div style={{ borderTop: "1px solid rgba(255,255,255,0.08)", paddingTop: "1.5rem", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "1rem" }}>
          <p style={{ color: "rgba(255,255,255,0.3)", fontSize: "0.78rem" }}>© {new Date().getFullYear()} The Health Plug. All rights reserved.</p>
          <div style={{ display: "flex", gap: "1.5rem", flexWrap: "wrap" }}>
            {["Privacy Policy", "POPIA Notice", "Terms of Use"].map(l => (
              <span key={l} style={{ color: "rgba(255,255,255,0.3)", fontSize: "0.78rem", cursor: "pointer" }}>{l}</span>
            ))}
          </div>
          <p style={{ color: "rgba(255,255,255,0.2)", fontSize: "0.72rem", fontStyle: "italic" }}>
            The information on this site is for educational purposes only and does not replace professional medical advice.
          </p>
        </div>
      </div>
    </footer>
  );
}
